import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Users, Building2, AlertTriangle, Flag, Ban } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * TRUST & SAFETY PAGE — FINAL DRAFT
 * 
 * Single authoritative source for:
 * - Enforcement stages
 * - Reporting process
 * - Child safety rules
 * - Sponsorship enforcement
 * 
 * Links from footer, signup, and FAQs.
 */
export default function TrustSafety() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center h-14">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-black/5" data-testid="trust-safety-back-btn">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <div className="flex-1 text-center">
              <h1 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Trust & Safety</h1>
            </div>
            <div className="w-9" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8" data-testid="trust-safety-content">
        {/* Title */}
        <section className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'var(--lk-sage-muted)' }}
            >
              <Shield className="w-6 h-6" style={{ color: 'var(--lk-primary-green)' }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
                Trust & Safety
              </h1>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                LikeKind is built around explicit limits.
              </p>
            </div>
          </div>
        </section>

        {/* What We Do / Don't Do */}
        <section className="mb-8 p-6 rounded-2xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
          <h2 className="font-semibold mb-4" style={{ color: 'var(--lk-primary-green)' }}>What we do</h2>
          <ul className="space-y-2 text-sm mb-6" style={{ color: 'var(--fy-text-secondary)' }}>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>Help people coordinate real-world activity</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>Provide calm, structured spaces for groups and institutions</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>Protect children and families by default</span>
            </li>
          </ul>
          <h2 className="font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>What we don&apos;t do</h2>
          <ul className="space-y-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <li>✕ Track behavior for engagement</li>
            <li>✕ Sell or share user data</li>
            <li>✕ Allow hidden promotion or influence</li>
            <li>✕ Use feeds, streaks, or metrics to keep people scrolling</li>
          </ul>
        </section>

        {/* ENFORCEMENT STAGES */}
        <section className="mb-8 p-6 rounded-2xl" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }} data-testid="enforcement-stages">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5" style={{ color: 'var(--lk-secondary-orange)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Enforcement Stages</h2>
          </div>
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Policy violations follow a 3-stage enforcement model:
          </p>
          <div className="space-y-4">
            <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
              <h3 className="font-medium text-sm mb-1" style={{ color: 'var(--lk-secondary-orange)' }}>Stage 1: Warning</h3>
              <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                Content removal. Account flagged. For sponsors: payment forfeited for that cycle.
              </p>
            </div>
            <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
              <h3 className="font-medium text-sm mb-1" style={{ color: '#DC2626' }}>Stage 2: Suspension</h3>
              <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                Account suspended. 6-month lockout period. Review required before reinstatement.
              </p>
            </div>
            <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
              <h3 className="font-medium text-sm mb-1" style={{ color: '#7F1D1D' }}>Stage 3: Permanent Ban</h3>
              <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                Account permanently removed. No refunds. No reinstatement.
              </p>
            </div>
          </div>
          <p className="text-xs mt-4 font-medium" style={{ color: 'var(--fy-text-disabled)' }}>
            Child safety violations skip directly to Stage 3. No exceptions.
          </p>
        </section>

        {/* REPORTING PROCESS */}
        <section className="mb-8 p-6 rounded-2xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }} data-testid="reporting-process">
          <div className="flex items-center gap-2 mb-4">
            <Flag className="w-5 h-5" style={{ color: 'var(--lk-primary-green)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Reporting Process</h2>
          </div>
          <div className="space-y-3 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <p><strong>1. Report:</strong> Use the report button on any content or profile.</p>
            <p><strong>2. Review:</strong> All reports are reviewed by the policy team.</p>
            <p><strong>3. Action:</strong> Enforcement action is taken within 7 days.</p>
            <p><strong>4. Notification:</strong> Reporter receives confirmation of action taken.</p>
          </div>
          <div className="mt-4 p-3 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
            <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
              Reports are confidential. The reported party does not see who reported them.
            </p>
          </div>
        </section>

        {/* CHILD SAFETY RULES */}
        <section className="mb-8 p-6 rounded-2xl" style={{ backgroundColor: 'var(--lk-sage-muted)', border: '1px solid var(--lk-primary-green)' }} data-testid="child-safety-rules">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5" style={{ color: 'var(--lk-primary-green)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--lk-primary-green)' }}>Child Safety Rules</h2>
          </div>
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-primary)' }}>
            Child safety overrides all other considerations.
          </p>
          <ul className="space-y-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>Students do not have public profiles</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>Students are not searchable or discoverable</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>No student-to-student messaging</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>No ads, embeds, or external scripts</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>Parent visibility is enforced where required</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>Child-directed content from businesses is prohibited</span>
            </li>
          </ul>
          <div className="mt-4 p-3 rounded-xl" style={{ backgroundColor: 'rgba(122, 143, 91, 0.2)' }}>
            <p className="text-xs font-medium" style={{ color: 'var(--lk-primary-green)' }}>
              Child safety violations result in immediate permanent ban. No exceptions. No appeals.
            </p>
          </div>
        </section>

        {/* SPONSORSHIP ENFORCEMENT */}
        <section className="mb-8 p-6 rounded-2xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }} data-testid="sponsorship-enforcement">
          <div className="flex items-center gap-2 mb-4">
            <Ban className="w-5 h-5" style={{ color: 'var(--lk-earth-brown)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Sponsorship Enforcement</h2>
          </div>
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Sponsors are held to higher standards than regular users.
          </p>
          <ul className="space-y-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <li><strong>Manual review:</strong> Every placement is human-reviewed before approval.</li>
            <li><strong>No changes:</strong> Content is immutable once approved. Changes require re-approval.</li>
            <li><strong>No targeting:</strong> No behavioral, demographic, or interest-based targeting.</li>
            <li><strong>No analytics:</strong> Sponsors receive no user data, views, or clicks.</li>
            <li><strong>Financial consequences:</strong> Violations result in payment forfeiture.</li>
          </ul>
          <div className="mt-4 p-3 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
            <p className="text-xs font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Prohibited sponsor content:
            </p>
            <ul className="text-xs mt-2 space-y-1" style={{ color: 'var(--fy-text-secondary)' }}>
              <li>• Child-directed advertising</li>
              <li>• Political manipulation</li>
              <li>• Misleading claims</li>
              <li>• External tracking or embeds</li>
              <li>• Bait-and-switch content</li>
            </ul>
          </div>
        </section>

        {/* INSTITUTIONAL ENFORCEMENT */}
        <section className="mb-8 p-6 rounded-2xl" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5" style={{ color: 'var(--lk-earth-brown)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Institutional Use</h2>
          </div>
          <p className="text-sm mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
            Schools and institutions agree to automated and manual policy enforcement. Decisions are final.
          </p>
          <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            Institutions may appear in discovery views for informational purposes only. No promotional ranking, targeting, or paid visibility is permitted.
          </p>
        </section>

        {/* TRANSPARENCY */}
        <section className="mb-8 p-6 rounded-2xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Transparency</h2>
          </div>
          <p className="text-sm mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
            If you see sponsored content, you can always see why it&apos;s there, what rules it follows, and how to report it.
          </p>
          <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
            Trust is not a claim on LikeKind. It&apos;s enforced through structure.
          </p>
        </section>

        {/* Related Links */}
        <section className="space-y-3" data-testid="related-policies">
          <h3 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Related policies</h3>
          <Button
            onClick={() => navigate('/content-guidelines')}
            variant="outline"
            className="w-full justify-start rounded-xl"
            style={{ borderColor: 'var(--fy-border-subtle)' }}
          >
            Content Guidelines →
          </Button>
          <Button
            onClick={() => navigate('/sponsor-policy')}
            variant="outline"
            className="w-full justify-start rounded-xl"
            style={{ borderColor: 'var(--fy-border-subtle)' }}
          >
            Sponsor Policy →
          </Button>
          <Button
            onClick={() => navigate('/group-policy')}
            variant="outline"
            className="w-full justify-start rounded-xl"
            style={{ borderColor: 'var(--fy-border-subtle)' }}
          >
            Group Policy →
          </Button>
          <Button
            onClick={() => navigate('/terms')}
            variant="outline"
            className="w-full justify-start rounded-xl"
            style={{ borderColor: 'var(--fy-border-subtle)' }}
          >
            Terms of Service →
          </Button>
          <Button
            onClick={() => navigate('/privacy')}
            variant="outline"
            className="w-full justify-start rounded-xl"
            style={{ borderColor: 'var(--fy-border-subtle)' }}
          >
            Privacy Policy →
          </Button>
        </section>

        {/* Last Updated */}
        <p className="text-center text-xs mt-8" style={{ color: 'var(--fy-text-disabled)' }}>
          Last updated: January 2026
        </p>
      </main>
    </div>
  );
}
